import { Dropdown, DropdownItem } from "@/design-system/Dropdown/Dropdown";
import Input from "@/design-system/Input/Input";
import { CategoryTab } from "@/design-system/Tabs/CategoryTab";
import {
  SBCCategory,
  SBCSetSortOption,
  getSbcSetCategoriesSelector,
} from "@/redux/sbcs/sbcSetsSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { ArrowDown, Search } from "lucide-react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

export const SBC_SORT_OPTIONS: DropdownItem[] = [
  {
    label: "Most Upvoted",
    value: "most-upvoted",
  },
  {
    label: "Price - High to Low",
    value: "price-high-to-low",
  },
  {
    label: "Price - Low to High",
    value: "price-low-to-high",
  },
  {
    label: "In-progress",
    value: "in-progress",
  },
  {
    label: "Expiration",
    value: "expiration",
  },
  {
    label: "Newest",
    value: "newest",
  },
  {
    label: "Meta Rating",
    value: "meta-rating",
  },
  {
    label: "Trending",
    value: "trending",
  },
];

/*
  The redux tables keep their English labels: the sort label types the state.
  EA categories (value > 0) already carry the localized name from the set
  list; the toolbar-only tabs and the sort options go through these maps.
*/
const CATEGORY_LABEL_KEYS = {
  All: "setList.categories.all",
  Favorites: "setList.categories.favorites",
} as const;

const SORT_LABEL_KEYS = {
  "Most Upvoted": "setList.sort.mostUpvoted",
  "Price - High to Low": "setList.sort.priceHighToLow",
  "Price - Low to High": "setList.sort.priceLowToHigh",
  "In-progress": "setList.sort.inProgress",
  Expiration: "setList.sort.expiration",
  Newest: "setList.sort.newest",
  "Meta Rating": "setList.sort.metaRating",
  Trending: "setList.sort.trending",
} as const;

/*
  Translated labels can outgrow the bar. Pieces give way in order until it
  fits: 1 the "Sort by" label, 2 the search field's spare width. Measured, not
  breakpointed, so a locale keeps whatever it has room for. Same scheme as
  NavBarMega's fitBar; the resize observer defers a frame because mutating
  inside the callback trips the ResizeObserver loop. A piece can drop its
  `data-squeeze` between renders (the search field while in use), so every
  squeeze mark is cleared, not only the current pieces'.
*/
const fitToolbar = (bar: HTMLDivElement | null) => {
  if (!bar) return;
  bar
    .querySelectorAll<HTMLElement>("[data-squeezed]")
    .forEach((piece) => delete piece.dataset.squeezed);
  const pieces = [...bar.querySelectorAll<HTMLElement>("[data-squeeze]")].sort(
    (a, b) => Number(a.dataset.squeeze) - Number(b.dataset.squeeze),
  );
  for (const piece of pieces) {
    if (bar.scrollWidth <= bar.clientWidth) break;
    piece.dataset.squeezed = "";
  }
};

type CategoryLabelKey =
  (typeof CATEGORY_LABEL_KEYS)[keyof typeof CATEGORY_LABEL_KEYS];
type SortLabelKey = (typeof SORT_LABEL_KEYS)[keyof typeof SORT_LABEL_KEYS];

export type SbcSetStatus = "all" | "available" | "completed" | "hidden";

const STATUS_LABEL_KEYS = {
  all: "setList.status.all",
  available: "setList.status.available",
  completed: "setList.status.completed",
  hidden: "setList.status.hidden",
} as const;

interface SbcSetsPageToolbarProps {
  currentSortBy: SBCSetSortOption;
  handleSortByChanged: (option: SBCSetSortOption) => void;
  handleSearch: (searchTerm: string) => void;
  searchTerm: string;
  currentCategory: SBCCategory;
  handleCategoryChanged: (category: SBCCategory) => void;
  status: SbcSetStatus;
  onStatusChanged: (status: SbcSetStatus) => void;
  statusCounts: Record<SbcSetStatus, number>;
}

export const SbcSetsPageToolbar = ({
  currentSortBy,
  handleSortByChanged,
  handleSearch,
  searchTerm,
  currentCategory,
  handleCategoryChanged,
  status,
  onStatusChanged,
  statusCounts,
}: SbcSetsPageToolbarProps) => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const [searchFocused, setSearchFocused] = useState(false);
  // A search field in use keeps its full width (the clear button needs it),
  // so it stops being a squeeze piece and the "Sort by" label goes first.
  const searchActive = searchFocused || searchTerm !== "";
  const barRef = useRef<HTMLDivElement>(null);
  // Before paint on every render, and a frame after any resize (see fitToolbar).
  useLayoutEffect(() => fitToolbar(barRef.current));
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => fitToolbar(bar));
    });
    observer.observe(bar);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);
  const { t } = useTranslation(["sbcs", "common"]);
  const categories = useSelector(getSbcSetCategoriesSelector);
  const categoryLabel = ({ label, value }: SBCCategory) => {
    if (value > 0) return label;
    const key = (CATEGORY_LABEL_KEYS as Record<string, CategoryLabelKey>)[
      label
    ];
    return key ? t(key) : label;
  };
  const sortLabel = (label: string) => {
    const key = (SORT_LABEL_KEYS as Record<string, SortLabelKey>)[label];
    return key ? t(key) : label;
  };
  const dropdownWidth = isTouchDevice ? 118 : 150;
  const sortWidth = isTouchDevice ? 118 : 180;
  const isCurrentCategory = (category: SBCCategory) =>
    category.value === currentCategory.value;
  const sbcSetCategories = useMemo(
    () =>
      categories.map((category) => (
        <CategoryTab
          key={category.value ?? category.label}
          label={categoryLabel(category)}
          isActive={isCurrentCategory(category)}
          onClick={() => handleCategoryChanged(category)}
        />
      )),
    [categories, currentCategory, handleCategoryChanged, t],
  );
  // Dropdown options carry the translated label; the index maps a pick back
  // to the redux entry, since "Favorites" has no value of its own.
  const categoryOptions: DropdownItem[] = categories.map((category, index) => ({
    label: categoryLabel(category),
    value: String(index),
  }));
  const selectedCategoryValue = String(categories.findIndex(isCurrentCategory));
  /* The count rides along on desktop, where the bar has room for it. On a
     phone the bar is already three controls wide and the label alone wins. */
  const statusOptions: DropdownItem[] = (
    Object.keys(STATUS_LABEL_KEYS) as SbcSetStatus[]
  ).map((value) => ({
    value,
    label: isTouchDevice
      ? t(STATUS_LABEL_KEYS[value])
      : `${t(STATUS_LABEL_KEYS[value])} (${statusCounts[value]})`,
  }));
  const sortOptions: DropdownItem[] = SBC_SORT_OPTIONS.map((option) => ({
    ...option,
    label: sortLabel(option.label),
  }));

  return (
    <div className="flex flex-col w-full relative">
      {/* The band is built here rather than with the shared Toolbar so its
          inset matches the grid below it: Toolbar steps its padding to 64px
          where the cards step to 32px, and the search sat proud of them. */}
      <div className="flex h-15 w-full flex-row justify-center bg-surface py-2 touch-device:py-4">
        <div className="mx-auto flex w-full max-w-content items-center px-4 large-desktop:px-8">
          <div
            ref={barRef}
            className="flex flex-row w-full h-full items-center gap-x-4 max-touch-device:gap-x-2"
          >
            <div className="flex flex-1 shrink-0 items-center">
              <div
                className="w-54 data-[squeezed]:w-36 max-touch-device:data-[squeezed]:w-11"
                data-squeeze={searchActive ? undefined : 2}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              >
                <Input
                  type="search"
                  size="small"
                  icon={<Search className="size-4" />}
                  placeholder={t("common:search")}
                  autoFocus={isMobile}
                  value={searchTerm}
                  onChange={handleSearch}
                  onClear={() => handleSearch("")}
                />
              </div>
            </div>
            <div className="flex flex-1 shrink-0 justify-end items-center text-sm gap-x-4 max-touch-device:gap-x-2 whitespace-nowrap">
              {!searchActive && (
                <div className="touch-device:hidden flex flex-row items-center">
                  <Dropdown
                    variant="secondary"
                    hideSearch
                    width={dropdownWidth}
                    items={categoryOptions}
                    selected={selectedCategoryValue}
                    placeholder={categoryLabel(currentCategory)}
                    onChange={(item) =>
                      handleCategoryChanged(categories[Number(item.value)])
                    }
                  />
                </div>
              )}
              {/* Status is its own axis, so it sits clear of the "Sort by" label
                — that label belongs to the control on its right. */}
              <Dropdown
                variant="secondary"
                hideSearch
                width={dropdownWidth}
                items={statusOptions}
                selected={status}
                placeholder={t(STATUS_LABEL_KEYS[status])}
                onChange={(item) => onStatusChanged(item.value as SbcSetStatus)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* A row of its own under the bar, lined up with the grid it labels.
          The gap either side is what separates the controls from the sets. */}
      <div className="mx-auto flex w-full max-w-content items-center gap-x-2 px-4 pt-3 large-desktop:px-8">
        {/* Pulled back by its own padding: the tabs have no box of their own,
            so it is the words that must line up with the cards, not the hit
            area around them. Same for the sort at the other edge. */}
        {!isTouchDevice && (
          <div className="-ml-2 flex items-center gap-x-2">
            {sbcSetCategories}
          </div>
        )}
        <div className="flex-1" />
        {/* What the grid is ordered by, stated rather than labelled, the way
            the mobile list header does it. The words are the control. */}
        <Dropdown
          variant="secondary"
          hideSearch
          align="end"
          width={sortWidth}
          items={sortOptions}
          selected={currentSortBy.value}
          placeholder={sortLabel(currentSortBy.label)}
          onChange={(item) =>
            handleSortByChanged(
              SBC_SORT_OPTIONS.find(
                (sort) => sort.value === item.value,
              ) as SBCSetSortOption,
            )
          }
          trigger={
            <button
              type="button"
              aria-label={t("setList.sortedBy", {
                sort: sortLabel(currentSortBy.label),
              })}
              className="-mr-1 flex items-center gap-x-1 px-1 py-1.5 text-sm text-foreground-muted hover:text-foreground"
            >
              {sortLabel(currentSortBy.label)}
              <ArrowDown className="size-4" />
            </button>
          }
        />
      </div>
    </div>
  );
};
